// ✅ src/components/screens/NotificationsScreen.js
// ✅ All-in-one: tap notification opens READ MODAL (no new screen)
// ✅ No browser confirm popups (no "localhost says") -> in-app confirm modal

import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../../lib/firebase";
import {
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

function safeText(v) {
  return String(v ?? "").trim();
}

function toDate(ts) {
  try {
    if (!ts) return null;
    if (ts?.toDate) return ts.toDate();
    return new Date(ts);
  } catch {
    return null;
  }
}

function timeAgo(ts) {
  const d = toDate(ts);
  if (!d) return "";
  const diff = Date.now() - d.getTime();
  const s = Math.floor(diff / 1000);
  if (s < 10) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function fmtDateTime(ts) {
  const d = toDate(ts);
  if (!d) return "—";
  try {
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

function typeMeta(type) {
  const t = safeText(type).toUpperCase();

  if (t.includes("APPROV")) return { icon: "✅", label: "Approved" };
  if (t.includes("REJECT")) return { icon: "❌", label: "Rejected" };
  if (t.includes("CHANGES")) return { icon: "✏️", label: "Changes" };
  if (t.includes("SALE") || t.includes("ORDER")) return { icon: "💰", label: "Order" };
  if (t.includes("PAYOUT")) return { icon: "🏦", label: "Payout" };
  if (t.includes("MESSAGE") || t.includes("CHAT")) return { icon: "💬", label: "Message" };
  if (t.includes("WARN")) return { icon: "⚠️", label: "Warning" };
  if (t.includes("SYSTEM")) return { icon: "🔔", label: "System" };

  return { icon: "🔔", label: t ? t.replaceAll("_", " ") : "Notification" };
}

export default function NotificationsScreen({ goBack, onOpen }) {
  const uid = auth.currentUser?.uid || "";

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // ✅ READ MODAL state
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  // ✅ CONFIRM MODAL state (delete / clear)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const unreadCount = useMemo(
    () => rows.filter((n) => !Boolean(n.read)).length,
    [rows]
  );

  useEffect(() => {
    if (!uid) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErr("");
    setMsg("");

    const qy = query(
      collection(db, "notifications", uid, "items"),
      orderBy("createdAt", "desc"),
      limit(80)
    );

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows(list);
        setLoading(false);
      },
      (e) => {
        console.error(e);
        setErr(e?.message || "Failed to load notifications");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [uid]);

  async function markRead(id) {
    if (!uid || !id) return;
    setErr("");
    try {
      await updateDoc(doc(db, "notifications", uid, "items", id), {
        read: true,
        readAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to mark as read");
    }
  }

  async function toggleRead(n) {
    if (!uid || !n?.id) return;
    setErr("");
    try {
      const next = !Boolean(n.read);
      await updateDoc(doc(db, "notifications", uid, "items", n.id), {
        read: next,
        readAt: next ? serverTimestamp() : null,
      });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to update");
    }
  }

  async function markAllRead() {
    if (!uid) return;
    setErr("");
    setMsg("");

    try {
      const unread = rows.filter((r) => !Boolean(r.read)).slice(0, 450);
      if (!unread.length) return setMsg("✅ Nothing to mark");

      const batch = writeBatch(db);
      unread.forEach((n) => {
        batch.update(doc(db, "notifications", uid, "items", n.id), {
          read: true,
          readAt: serverTimestamp(),
        });
      });
      await batch.commit();
      setMsg("✅ Marked all as read");
      setTimeout(() => setMsg(""), 900);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to mark all");
    }
  }

  // ✅ IN-APP confirm helper
  function openConfirm(text, action) {
    setConfirmText(text);
    setConfirmAction(() => action);
    setConfirmOpen(true);
  }
  function closeConfirm() {
    if (confirmBusy) return;
    setConfirmOpen(false);
    setConfirmText("");
    setConfirmAction(null);
  }

  async function deleteOne(id) {
    if (!uid || !id) return;

    openConfirm("Delete this notification?", async () => {
      setConfirmBusy(true);
      setErr("");
      try {
        await deleteDoc(doc(db, "notifications", uid, "items", id));
        // if we are reading this one, close reader
        if (active?.id === id) {
          setOpen(false);
          setActive(null);
        }
      } catch (e) {
        console.error(e);
        setErr(e?.message || "Failed to delete");
      } finally {
        setConfirmBusy(false);
        closeConfirm();
      }
    });
  }

  async function clearAll() {
    if (!uid) return;

    openConfirm("Clear ALL notifications? This can’t be undone.", async () => {
      setConfirmBusy(true);
      setErr("");
      setMsg("");

      try {
        const list = rows.slice(0, 450);
        if (!list.length) {
          setConfirmBusy(false);
          closeConfirm();
          return;
        }

        const batch = writeBatch(db);
        list.forEach((n) =>
          batch.delete(doc(db, "notifications", uid, "items", n.id))
        );
        await batch.commit();

        setMsg("✅ Cleared");
        setTimeout(() => setMsg(""), 900);

        setOpen(false);
        setActive(null);
      } catch (e) {
        console.error(e);
        setErr(e?.message || "Failed to clear");
      } finally {
        setConfirmBusy(false);
        closeConfirm();
      }
    });
  }

  function openAction(n) {
    // ✅ mark read immediately
    if (!n.read) markRead(n.id);

    // ✅ show reader modal
    setActive(n);
    setOpen(true);

    // ✅ optional navigation hook (kept)
    const data = n.data || {};
    const productId = data.productId || n.productId || "";
    const orderId = data.orderId || n.orderId || "";
    const chatId = data.chatId || n.chatId || "";

    if (typeof onOpen === "function") {
      if (productId) return onOpen({ screen: "ProductDetail", productId });
      if (orderId) return onOpen({ screen: "OrderDetail", orderId });
      if (chatId) return onOpen({ screen: "Chat", chatId });
    }
  }

  const activeMeta = useMemo(() => typeMeta(active?.type), [active?.type]);

  return (
    <div style={S.page}>
      <div style={S.top}>
        <button onClick={goBack} style={S.back} type="button">
          ←
        </button>

        <div style={{ minWidth: 0 }}>
          <div style={S.titleRow}>
            <div style={S.title}>Notifications</div>
            {unreadCount ? (
              <span style={S.unreadPill}>{unreadCount} unread</span>
            ) : null}
          </div>
          <div style={S.sub}>
            Updates about approvals, sales, payouts, messages, and safety.
          </div>
        </div>

        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={markAllRead} style={S.smallBtn} type="button">
            Mark all read
          </button>
          <button onClick={clearAll} style={S.smallBtnDanger} type="button">
            Clear
          </button>
        </div>
      </div>

      {loading ? <div style={S.note}>Loading…</div> : null}
      {msg ? <div style={S.msg}>{msg}</div> : null}
      {err ? <div style={S.err}>{err}</div> : null}

      {!loading && rows.length === 0 ? (
        <div style={S.note}>No notifications yet.</div>
      ) : null}

      <div style={S.list}>
        {rows.map((n) => {
          const meta = typeMeta(n.type);
          const isUnread = !Boolean(n.read);

          return (
            <div
              key={n.id}
              style={{ ...S.card, ...(isUnread ? S.cardUnread : null) }}
              onClick={() => openAction(n)}
              role="button"
              tabIndex={0}
            >
              <div style={S.cardTop}>
                <div style={S.left}>
                  <div style={S.icon}>{meta.icon}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={S.cardTitleRow}>
                      <div style={S.cardTitle}>
                        {safeText(n.title) || meta.label}
                      </div>
                      {isUnread ? <span style={S.dot} /> : null}
                    </div>

                    {n.message ? (
                      <div style={S.cardBody}>{safeText(n.message)}</div>
                    ) : null}

                    <div style={S.metaRow}>
                      <span style={S.typePill}>{meta.label}</span>
                      <span style={S.time}>{timeAgo(n.createdAt)}</span>
                    </div>
                  </div>
                </div>

                <div style={S.actions}>
                  <button
                    style={S.tinyBtn}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRead(n);
                    }}
                    title="Toggle read"
                  >
                    {isUnread ? "Read" : "Unread"}
                  </button>

                  <button
                    style={S.tinyBtnDanger}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteOne(n.id);
                    }}
                    title="Delete"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ✅ READ MODAL */}
      {open && active ? (
        <div style={S.modalOverlay} onClick={() => { setOpen(false); setActive(null); }}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalTop}>
              <div style={S.modalIcon}>{activeMeta.icon}</div>
              <div style={{ minWidth: 0 }}>
                <div style={S.modalTitle}>{safeText(active.title) || activeMeta.label}</div>
                <div style={S.modalSub}>
                  {activeMeta.label} • {fmtDateTime(active.createdAt)} • {timeAgo(active.createdAt)}
                </div>
              </div>
            </div>

            {active.message ? (
              <div style={S.modalBody}>{safeText(active.message)}</div>
            ) : (
              <div style={S.modalBodyMuted}>No message content.</div>
            )}

            <div style={S.modalActions}>
              <button
                style={S.smallBtn}
                type="button"
                onClick={() => toggleRead(active)}
              >
                {active.read ? "Mark Unread" : "Mark Read"}
              </button>

              <button
                style={S.smallBtnDanger}
                type="button"
                onClick={() => deleteOne(active.id)}
              >
                Delete
              </button>

              <button
                style={S.smallBtn}
                type="button"
                onClick={() => { setOpen(false); setActive(null); }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ✅ CONFIRM MODAL (replaces window.confirm) */}
      {confirmOpen ? (
        <div style={S.modalOverlay} onClick={closeConfirm}>
          <div style={S.confirmModal} onClick={(e) => e.stopPropagation()}>
            <div style={S.confirmTitle}>Confirm</div>
            <div style={S.confirmText}>{confirmText}</div>

            <div style={S.modalActions}>
              <button style={S.smallBtn} type="button" onClick={closeConfirm} disabled={confirmBusy}>
                Cancel
              </button>
              <button
                style={S.smallBtnDanger}
                type="button"
                onClick={() => {
                  if (typeof confirmAction === "function") confirmAction();
                }}
                disabled={confirmBusy}
              >
                {confirmBusy ? "Working…" : "Yes"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    padding: 14,
    background: "linear-gradient(180deg, #f4f0ff 0%, #edf2ff 45%, #f8fafc 100%)",
  },
  top: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  back: {
    width: 42,
    height: 42,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
    cursor: "pointer",
    fontWeight: 900,
  },
  titleRow: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  title: { fontWeight: 950, fontSize: 18, color: "#0f172a" },
  unreadPill: {
    fontSize: 11,
    fontWeight: 950,
    padding: "5px 10px",
    borderRadius: 999,
    background: "rgba(99,102,241,0.12)",
    border: "1px solid rgba(99,102,241,0.22)",
  },
  sub: { marginTop: 2, fontSize: 12, fontWeight: 800, color: "rgba(15,23,42,0.60)" },

  note: {
    padding: 12,
    borderRadius: 12,
    background: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(15,23,42,0.08)",
    fontWeight: 850,
    marginBottom: 12,
  },
  msg: {
    padding: 12,
    borderRadius: 12,
    background: "rgba(34,197,94,0.10)",
    border: "1px solid rgba(34,197,94,0.18)",
    fontWeight: 950,
    marginBottom: 12,
  },
  err: {
    padding: 12,
    borderRadius: 12,
    background: "rgba(239,68,68,0.10)",
    border: "1px solid rgba(239,68,68,0.18)",
    color: "#7f1d1d",
    fontWeight: 950,
    marginBottom: 12,
  },

  list: { display: "grid", gap: 12 },

  card: {
    padding: 12,
    borderRadius: 16,
    background: "rgba(255,255,255,0.9)",
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
    cursor: "pointer",
  },
  cardUnread: {
    border: "1px solid rgba(99,102,241,0.22)",
    background: "rgba(255,255,255,0.96)",
  },

  cardTop: { display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between" },
  left: { display: "flex", gap: 12, alignItems: "flex-start", minWidth: 0, flex: 1 },

  icon: {
    width: 42,
    height: 42,
    borderRadius: 16,
    display: "grid",
    placeItems: "center",
    background: "rgba(99,102,241,0.12)",
    border: "1px solid rgba(15,23,42,0.08)",
    fontSize: 18,
    flexShrink: 0,
  },

  cardTitleRow: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
  cardTitle: { fontWeight: 950, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  dot: { width: 8, height: 8, borderRadius: 999, background: "#6366f1" },

  cardBody: { marginTop: 6, fontWeight: 800, color: "rgba(15,23,42,0.75)" },

  metaRow: { display: "flex", gap: 10, alignItems: "center", marginTop: 8, flexWrap: "wrap" },
  typePill: {
    fontSize: 11,
    fontWeight: 950,
    padding: "4px 8px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(2,6,23,0.03)",
  },
  time: { fontSize: 12, fontWeight: 850, opacity: 0.65 },

  actions: { display: "grid", gap: 8, flexShrink: 0 },
  tinyBtn: {
    padding: "8px 10px",
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 12,
  },
  tinyBtnDanger: {
    padding: "8px 10px",
    borderRadius: 12,
    border: "1px solid rgba(239,68,68,0.18)",
    background: "rgba(239,68,68,0.10)",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 12,
    color: "#7f1d1d",
  },

  smallBtn: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 12,
  },
  smallBtnDanger: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(239,68,68,0.18)",
    background: "rgba(239,68,68,0.10)",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 12,
    color: "#7f1d1d",
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,.55)",
    display: "grid",
    placeItems: "center",
    padding: 16,
    zIndex: 9999,
  },
  modal: {
    width: "min(520px, 100%)",
    background: "white",
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    boxShadow: "0 25px 70px rgba(0,0,0,.22)",
    padding: 14,
    display: "grid",
    gap: 12,
  },
  confirmModal: {
    width: "min(420px, 100%)",
    background: "white",
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    boxShadow: "0 25px 70px rgba(0,0,0,.22)",
    padding: 14,
    display: "grid",
    gap: 10,
  },
  modalTop: { display: "flex", gap: 12, alignItems: "flex-start" },
  modalIcon: {
    width: 44,
    height: 44,
    borderRadius: 16,
    display: "grid",
    placeItems: "center",
    background: "rgba(99,102,241,0.12)",
    border: "1px solid rgba(15,23,42,0.08)",
    fontSize: 20,
    flexShrink: 0,
  },
  modalTitle: { fontWeight: 950, fontSize: 16, color: "#0f172a" },
  modalSub: { marginTop: 4, fontSize: 12, fontWeight: 850, color: "rgba(15,23,42,0.60)" },
  modalBody: { fontWeight: 850, color: "rgba(15,23,42,0.82)", lineHeight: 1.45, whiteSpace: "pre-wrap" },
  modalBodyMuted: { fontWeight: 850, color: "rgba(15,23,42,0.55)" },
  modalActions: { display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" },

  confirmTitle: { fontWeight: 950, fontSize: 16, color: "#0f172a" },
  confirmText: { fontWeight: 850, color: "rgba(15,23,42,0.80)", lineHeight: 1.4 },
};
